import type { UserDataSourcePreferenceItem } from "api/dataSource";

/**
 * A short, stable fingerprint of one of the org's ordered data-source
 * preferences.
 *
 * eva-map-svc resolves which sources a base well tile is built from server-side,
 * from the caller's JWT and org preference, so the tile URL never named a source
 * and a preference change produced a byte-identical URL. Mapbox GL caches tiles
 * by URL and never re-requests one it already holds, so the map kept showing
 * tiles built under the old preference until a full page reload replaced the
 * map. This token puts the preference back into the URL so a change produces a
 * different one, which is what makes the source refetch.
 *
 * eva-map-svc actually keys a base well tile on TWO separate preference
 * orders (`BaseWellTileController.HashPreferenceOrder`'s own callers): the
 * survey-source order (`Category.SurveyData`) and the header-source order
 * (`Category.HeaderData`) — a distinct, separately-settable preference (see
 * `useHeaderDataSources`) that decides which source's `Header.ResourcePlay`
 * wins. `buildSurveyPreferenceToken` and `buildHeaderPreferenceToken` below
 * hash each independently, on purpose: the MVT cache key
 * (`BuildCacheKey`) carries both, but the raster cache key
 * (`BuildRasterCacheKey`) deliberately omits the header order — a one-colour
 * raster reads no attributes, so folding the header token into ITS url would
 * force a needless raster refetch on every header-preference change. Callers
 * pick whichever token(s) apply to the URL they're building.
 *
 * It must be DETERMINISTIC — the same preference has to yield the same token on
 * every render, in every tab and after a reload. A counter or timestamp would
 * also bust the cache, but it would bust it on every mount, defeating ETag/304
 * revalidation and re-downloading every visible tile for no reason.
 *
 * Both the order and the on/off state matter: reordering changes which source
 * wins a well, and deactivating one removes its wells entirely.
 */
function fingerprintPreference(
  sources: UserDataSourcePreferenceItem[] | undefined
): string {
  if (!sources?.length) return "";

  // Copy before sorting — the array belongs to react-query's cache.
  const signature = [...sources]
    .sort((a, b) => a.order - b.order)
    .map((source) => `${source.sourceDb}:${source.isActive ? 1 : 0}`)
    .join(",");

  // FNV-1a (32-bit). Chosen over String.prototype hashing for the same reason
  // eva-map-svc avoids .NET's string hash for its thinning key: this has to be
  // a pure function of the input, identical across tabs, reloads and browsers.
  // Math.imul keeps the multiply in 32-bit range; >>> 0 keeps it unsigned.
  let hash = 0x811c9dc5;
  for (let i = 0; i < signature.length; i++) {
    hash ^= signature.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16).padStart(8, "0");
}

/** Fingerprint of the org's ordered SURVEY-source preference (`Category.SurveyData`). */
export function buildSurveyPreferenceToken(
  sources: UserDataSourcePreferenceItem[] | undefined
): string {
  return fingerprintPreference(sources);
}

/** Fingerprint of the org's ordered HEADER-source preference (`Category.HeaderData`). */
export function buildHeaderPreferenceToken(
  sources: UserDataSourcePreferenceItem[] | undefined
): string {
  return fingerprintPreference(sources);
}
